import React, { useEffect } from 'react';

import { Announcement } from 'mintel-react-ui';
import { Link, Route } from 'wouter';
import SidebarCallback from './SidebarCallback';

// This component is just for testing changing taskbar panels.
const testPanelArgs: Panels = {
  HelpPanel: { path: '/help/' },
  SharePanel: { path: '/share/' },
  FeedbackPanel: { path: '/feedback', content: { stuff: 'stuff' } },
};

const ShowPanel = ({
  configureSidebar,
  type,
}: {
  configureSidebar: ConfigureSidebarFn;
  type: string;
}) => {
  useEffect(() => {
    return configureSidebar({ [type]: testPanelArgs[type] }, { product: type });
  }, [configureSidebar, type]);

  return <div style={{ padding: '100px' }}>Showing {type}</div>;
};

const CustomizeNav = ({
  configureNavigation,
}: {
  configureNavigation: configureNavigationFn;
}): JSX.Element => {
  useEffect(() => {
    configureNavigation({
      stickyElements: (
        <Announcement>
          This is a sticky element added by the page and not the shell.
        </Announcement>
      ),
    });
    // Reset nav props on unmount. Don't do this in actual shell apps
    // (FederationWrapper resets it for you).
    return () => {
      configureNavigation({});
    };
  }, [configureNavigation]);

  return (
    <div style={{ padding: '100px' }}>
      Look at that announcement right below the header. ☝
    </div>
  );
};

/**
 * Component for testing error reporting.
 */
export const TestError = ({
  status,
  message,
  extraParams,
}: {
  status?: number;
  message: string;
  extraParams?: { [key: string]: unknown };
}): JSX.Element => {
  let httpError: CustomHttpError;

  if (status && message) {
    httpError = new Error(message);
    httpError.statusCode = status;
  } else {
    httpError = new Error('Any other test error!');
    httpError.statusCode = 500;
  }

  if (extraParams) {
    httpError.extraParams = extraParams;
  }

  useEffect(() => {
    throw httpError;
  }, [httpError]);

  return (
    <div>
      {status}, {message}
    </div>
  );
};

TestError.defaultProps = {
  status: 500,
};

/**
 * Component for testing pages we want hidden.
 */
export const TestPages = (): JSX.Element => (
  <div>
    <span>Test Federated Pages:</span>
    <ul>
      <li>
        <Link href="/helpPanel">Help Panel</Link>
      </li>
      <li>
        <Link href="/sharePanel">Share Panel</Link>
      </li>
      <li>
        <Link href="/defaultPanel">Default Panel</Link>
      </li>
      <li>
        <Link href="/customNav">Custom mintel-nav</Link>
      </li>
      <li>
        <Link href="/hello-frontend">Hello Frontend</Link>
      </li>
      <li>
        <Link href="/hello-mgl">Hello MGL</Link>
      </li>
    </ul>
    <span>Test Error Pages:</span>
    <ul>
      <li>
        <Link href="/500-error">500 Error Page</Link>
      </li>
      <li>
        <Link href="/503-error">503 Error Page</Link>
      </li>
      <li>
        <Link href="/403-error">403 Error Page</Link>
      </li>
      <li>
        <Link href="/404-error">404 Error Page</Link>
      </li>
    </ul>
  </div>
);

const sidebarCallbackDefaultProps = {
  portalJwt: 'portalJwt',
  oAuthAccessToken: 'oAuthAccessToken',
  hideIntercom: false,
};

export const getTestRoutes = (
  configureNavigation: configureNavigationFn,
): JSX.Element[] => [
  <Route path="/defaultPanel" key="defaultPanel">
    <SidebarCallback {...sidebarCallbackDefaultProps}>
      {(configureSidebar: ConfigureSidebarFn) => (
        <ShowPanel configureSidebar={configureSidebar} type="FeedbackPanel" />
      )}
    </SidebarCallback>
  </Route>,
  <Route path="/helpPanel" key="helpPanel">
    <SidebarCallback {...sidebarCallbackDefaultProps}>
      {(configureSidebar: ConfigureSidebarFn) => (
        <ShowPanel configureSidebar={configureSidebar} type="HelpPanel" />
      )}
    </SidebarCallback>
  </Route>,
  <Route path="/sharePanel" key="sharePanel">
    <SidebarCallback {...sidebarCallbackDefaultProps}>
      {(configureSidebar: ConfigureSidebarFn) => (
        <ShowPanel configureSidebar={configureSidebar} type="SharePanel" />
      )}
    </SidebarCallback>
  </Route>,
  <Route path="/customNav" key="customNav">
    <CustomizeNav configureNavigation={configureNavigation} />
  </Route>,
];
